import React from 'react';
import { withTheme } from 'styled-components';

import { IThemeObject } from 'pods/theme';
import { JssTextType } from 'core/utils/sitecore-utils';
import { Icon, ICONS } from 'core/components/Icon';
import * as Typo from 'core/styles/Typography';

import * as S from './ContactMethod.style';

// -------------------------
// Component atoms

/**
 * Displays the circle/icon combo driven by the primary theme.
 */
const Circle = ({ bgColor, icon }: { bgColor: string; icon: ICONS }) => (
	<S.Circle {...{ bgColor }}>
		<Icon size={23} name={icon} />
	</S.Circle>
);

/**
 * Title of `ContactMethod`; accepting either a JSS Text component, or a string.
 * (Depending on whether the ContactMethod is itself wrapped in a link or not)
 */
export const Title = ({
	children,
	jssText,
	isActive = true,
	...typoProps
}: {
	children?: React.ReactNode;
	jssText?: JssTextType;
	isActive?: boolean;
} & Typo.ITypoProps) => (
	<Typo.Custom
		StyledComponent={S.Title}
		classNameIdentifier="contactUsMethod--title"
		data-target-id="footer--authenticated--contact-link--title"
		color={isActive ? 'active' : 'default'}
		margin="0"
		jss={!!jssText?.value ? jssText : undefined}
		{...typoProps}
		tag="h4"
	>
		{!jssText && children}
	</Typo.Custom>
);

/**
 * Text of `ContactMethod`; accepting either JSS Text component or allowing a custom
 * render method to faciliate LivePerson which needs finer-grain control to conditionally
 * render things like public holiday vs online status vs offline status messages.
 */
export const Text = ({ content }: { content: JssTextType }) => (
	<Typo.Custom
		StyledComponent={S.Text}
		classNameIdentifier="contactUsMethod--text"
		data-target-id="footer--authenticated--contact-link--title"
		isRichText
		color="subdued"
		jss={content}
		margin="0"
	/>
);

// -------------------------

interface IProps {
	icon: ICONS;
	isActive?: boolean;
	children: React.ReactNode;
	theme: IThemeObject;
}

const ContactMethod = withTheme(
	({ icon, isActive = true, children, theme }: IProps) => {
		const bgColor = isActive
			? theme.global.colors.secondary.id
			: theme.global.colors.disabled.id;

		return (
			<S.Content {...{ bgColor, isActive }}>
				<Circle {...{ bgColor, icon }} />
				<div>{children}</div>
			</S.Content>
		);
	},
);

export default ContactMethod;
